import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SideMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  -webkit-tap-highlight-color: transparent;
`;

const SideMenuContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: auto;
  transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
  width: min(88vw, 350px);
  max-width: 100vw;
  height: 100%;
  height: 100dvh;
  background-color: white;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  transition: transform 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 0);
`;

const SideMenuHeader = styled.div`
  background-color: #232f3e;
  color: white;
  padding: 1rem 1.5rem;
  padding-top: calc(1rem + env(safe-area-inset-top, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const SideMenuTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MenuContent = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const MenuLink = styled(Link)`
  display: block;
  padding: 1rem 1.5rem;
  min-height: 48px;
  box-sizing: border-box;
  color: #333;
  text-decoration: none;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.3;
  transition: background-color 0.2s, color 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:active {
    background-color: #f5f5f5;
    color: var(--primary-color);
  }
`;

const LogoutButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 1rem 1.5rem;
  min-height: 48px;
  box-sizing: border-box;
  background: none;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  color: var(--error-color, #dc2626);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:active {
    background-color: #f5f5f5;
  }
`;

const GuestAuthButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
`;

const AuthLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1rem;
  border: 2px solid var(--primary-color);
  border-radius: 0.375rem;
  background: transparent;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }
`;

const CreateAccountLink = styled(AuthLink)`
  background-color: var(--primary-color);
  color: #fff;

  &:hover {
    background-color: var(--primary-dark, #059669);
    border-color: var(--primary-dark, #059669);
    color: #fff;
  }
`;

export const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:active {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

export const HamburgerLine = styled.div`
  width: 20px;
  height: 2px;
  background-color: var(--text-primary);
  border-radius: 1px;
`;

const SideMenu = ({
  isOpen: externalIsOpen,
  onToggle,
  isAuthenticated = false,
  isCourier = false,
  isAdmin = false,
  onLogout,
}) => {
  const { t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggle ? (value) => onToggle(value) : setInternalIsOpen;

  const close = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  return (
    <>
      <SideMenuOverlay isOpen={isOpen} onClick={close} />
      <SideMenuContainer isOpen={isOpen} aria-hidden={!isOpen}>
        <SideMenuHeader>
          <SideMenuTitle>{t('nav.menu', { defaultValue: 'Menu' })}</SideMenuTitle>
          <CloseButton onClick={close} aria-label="Close menu">
            ×
          </CloseButton>
        </SideMenuHeader>
        <MenuContent>
          <MenuLink to="/pre-owned-items" onClick={close}>
            {t('nav.preOwned')}
          </MenuLink>
          <MenuLink to="/designers" onClick={close}>
            {t('nav.designers')}
          </MenuLink>
          <MenuLink to="/boutiques" onClick={close}>
            {t('nav.boutiques')}
          </MenuLink>
          <MenuLink to="/artists" onClick={close}>
            {t('nav.artists')}
          </MenuLink>
          <MenuLink to="/wedding-essentials" onClick={close}>
            {t('nav.weddings')}
          </MenuLink>
          {isAuthenticated ? (
            <>
              <MenuLink to="/profile" onClick={close}>
                {t('nav.profile')}
              </MenuLink>
              <MenuLink to="/my-catalogue" onClick={close}>
                {t('nav.myCatalogue')}
              </MenuLink>
              <MenuLink to="/orders" onClick={close}>
                {t('nav.orders')}
              </MenuLink>
              <MenuLink to="/wallet-balance-and-aura" onClick={close}>
                {t('nav.auraWallet')}
              </MenuLink>
              <MenuLink to="/giftcards" onClick={close}>
                {t('nav.gifts')}
              </MenuLink>
              <MenuLink to="/favorites" onClick={close}>
                {t('nav.favoritesHeart')}
              </MenuLink>
              <MenuLink to="/negotiations" onClick={close}>
                {t('nav.messages')}
              </MenuLink>
              <MenuLink to="/interests" onClick={close}>
                {t('nav.interests')}
              </MenuLink>
              <MenuLink to="/what-to-offer" onClick={close}>
                {t('nav.whatToOffer')}
              </MenuLink>
              {isCourier && (
                <MenuLink to="/courier" onClick={close}>
                  {t('nav.courier')}
                </MenuLink>
              )}
              {isAdmin && (
                <MenuLink to="/admin" onClick={close}>
                  {t('nav.adminDashboard')}
                </MenuLink>
              )}
              <LogoutButton
                type="button"
                onClick={() => {
                  close();
                  onLogout?.();
                }}
              >
                {t('nav.logout')}
              </LogoutButton>
            </>
          ) : (
            <GuestAuthButtons>
              <CreateAccountLink to="/register" onClick={close}>
                {t('nav.createAccount')}
              </CreateAccountLink>
              <AuthLink to="/login" onClick={close}>
                {t('nav.login')}
              </AuthLink>
            </GuestAuthButtons>
          )}
        </MenuContent>
      </SideMenuContainer>
    </>
  );
};

export default SideMenu;
